/** PPB / Prelam MFC product variants (fallback when API unavailable). */
export const DEFAULT_PRELAM_PPB_MFC_VARIANT_OPTIONS = [
  { code: "PV001", name: "MFC Core" },
  { code: "PV002", name: "Core+" },
  { code: "PV003", name: "HMR" },
];

const normalizeLookupText = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const firstNonEmpty = (...values) => {
  for (const v of values) {
    const s = v == null ? "" : String(v).trim();
    if (s) return s;
  }
  return "";
};

const getMaterialDescriptionText = (product) =>
  firstNonEmpty(
    product?.MaterialDescription,
    product?.materialDescription,
    product?.ProductName,
    product?.productName,
    product?.ProductDescription,
    product?.productDescription,
    product?.ItemDescription,
    product?.itemDescription,
    product?.ProductCode,
    product?.productCode
  );

const parseEmissionFromText = (text) => {
  const upper = String(text || "").toUpperCase();
  if (!upper) return "";
  const direct = upper.match(/\bE[0-3]\b/);
  if (direct) return direct[0];
  if (upper.includes("CARB")) return "CARB";
  return "";
};

const parseFinishFromText = (text) => {
  const raw = String(text || "").trim();
  if (!raw) return "";
  const tokens = raw
    .split(/[-/|,]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  const skip = new Set(["PPB", "PRELAM", "MFC", "MAT", "O", "E0", "E1", "E2", "E3", "CARB"]);
  for (const token of tokens) {
    const upper = token.toUpperCase();
    if (skip.has(upper)) continue;
    if (/^E[0-3]$/.test(upper)) continue;
    if (/^PV\d+$/i.test(token)) continue;
    if (/mfc\s*core|core\+|hmr/i.test(token)) continue;
    if (/^\d+([.*xX]\d+)*$/.test(token)) continue;
    if (/^0*\d+\.?\d*$/.test(token)) continue;
    if (token.length >= 2 && token.length <= 24) return token;
  }
  return "";
};

const matchVariantFromText = (text, variantOptions) => {
  const normalized = normalizeLookupText(text);
  if (!normalized) return "";
  const options = (variantOptions || []).length
    ? variantOptions
    : DEFAULT_PRELAM_PPB_MFC_VARIANT_OPTIONS;
  const sorted = [...options].sort(
    (a, b) => String(b?.name || "").length - String(a?.name || "").length
  );
  for (const opt of sorted) {
    const name = String(opt?.name || opt?.Name || "").trim();
    const code = String(opt?.code || opt?.Code || "").trim();
    const nameNorm = normalizeLookupText(name);
    const codeNorm = normalizeLookupText(code);
    if (nameNorm && normalized.includes(nameNorm)) return name;
    if (codeNorm && normalized.includes(codeNorm)) return name || code;
  }
  if (normalized.includes("core+")) return "Core+";
  if (normalized.includes("mfc core")) return "MFC Core";
  if (/\bhmr\b/.test(normalized)) return "HMR";
  return "";
};

/**
 * Resolve Emission, Finish, and Product Grade & Variant primarily from material description,
 * with SAP invoice line fields as fallback.
 */
export function resolvePrelamPpbMfcInvoiceFields(product, variantOptions = []) {
  const materialText = getMaterialDescriptionText(product);
  const sapEmission = firstNonEmpty(product?.Emission, product?.emission, product?.Ph2);
  const sapFinish = firstNonEmpty(
    product?.ProductFinish,
    product?.productFinish,
    product?.Ph3
  );
  const sapVariant = firstNonEmpty(
    product?.ProductVariantName,
    product?.productVariantName,
    product?.ProductVariantCode,
    product?.productVariantCode
  );

  const parsedEmission = parseEmissionFromText(materialText);
  const parsedFinish = parseFinishFromText(materialText);
  const parsedVariant = matchVariantFromText(materialText, variantOptions);

  return {
    materialText,
    emission: parsedEmission || sapEmission,
    finish: parsedFinish || sapFinish,
    variantProductGrade: parsedVariant || sapVariant,
    designNumber: firstNonEmpty(product?.DesignNo, product?.designNo),
    size: firstNonEmpty(product?.Size, product?.size),
    thickness: firstNonEmpty(
      product?.ProductThickness,
      product?.productThickness,
      product?.Thickness
    ),
    quantity: firstNonEmpty(
      product?.InvoiceQuantity,
      product?.invoiceQuantity,
      product?.invoiceQty,
      product?.InvoiceQty
    ),
  };
}

export const matchesPpbMfcBusinessVerticalText = (text) => {
  const t = String(text || "").toLowerCase();
  if (!t.trim()) return false;
  return (
    t.includes("mfc") &&
    (t.includes("ppb") || t.includes("plain particle") || t.includes("particle board"))
  );
};
